import { useMemo, useState, useEffect, useRef, useCallback } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ButtonGroup,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Box,
  Button,
  CloseIcon,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
} from 'components/uikit';
import {
  DepositCredentials,
  DepositMethod,
  DepositBank,
  isSbpMethod,
  isQRMethod,
} from 'data/payments/models';
import { AnalyticsEvents, trackEvent } from 'utils/analytics';
import paymentInstructionCallouts from 'utils/gueds';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';

import { getBanksByMethod } from './BankDeeplinkButtons';
import { ImageSlideWithCallouts } from './ImageSlideWithCallouts';
import {
  DeviceType,
  InstructionSlide,
  getS3ImageUrl,
  getCalloutsFromJson,
} from './paymentInstructionUtils';

interface PaymentInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  depositId: string;
  method: DepositMethod | null;
  credentials: DepositCredentials;
  fromBank?: string | null;
  amount?: string | null;
  currency?: string | null;
}

export function PaymentInstructionsModal({
  isOpen,
  onClose,
  depositId,
  method,
  credentials,
  fromBank,
  amount,
  currency,
}: PaymentInstructionsModalProps) {
  const { t } = useTranslation('payments');
  const isMobile = useIsMobile();

  const [selectedDevice, setSelectedDevice] = useState<DeviceType>('android');
  const [selectedBank, setSelectedBank] = useState<DepositBank | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  // Swipe gesture state
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const availableBanks = useMemo(
    () => (method ? getBanksByMethod(method, fromBank ?? null) : []),
    [method, fromBank],
  );

  const isTgQr = method && ['VND_TG_QR', 'PHP_TG_QR'].includes(method);
  const tjsTransferType = useMemo(() => {
    if (credentials.type === 'CARD') return 'CARD' as const;
    return 'SBP' as const;
  }, [credentials.type]);

  const handleStepIndexChange = useCallback(
    (newStepIndex: number | ((prev: number) => number)) => {
      setStepIndex((prev) => {
        const nextStep =
          typeof newStepIndex === 'function'
            ? newStepIndex(prev)
            : newStepIndex;

        if (prev === nextStep) return prev;

        trackEvent(AnalyticsEvents.INSTRUCTIONS_STEP_VIEWED, {
          order_id: depositId,
          payment_method: method ?? '',
          step: String(nextStep),
          device: selectedDevice,
        });

        return nextStep;
      });
    },
    [depositId, method, selectedDevice],
  );

  const handleClose = () => {
    trackEvent(AnalyticsEvents.INSTRUCTIONS_CLOSED, {
      order_id: depositId,
      payment_method: method ?? '',
      device: selectedDevice,
      last_step: String(stepIndex),
    });
    onClose();
  };

  // Set default bank: prefer fromBank if provided, otherwise prefer Сбербанк, otherwise first available
  useEffect(() => {
    if (!selectedBank && availableBanks.length > 0) {
      if (fromBank) {
        // If fromBank is provided, use it
        const bankFromProp = availableBanks.find((bank) => bank === fromBank);
        if (bankFromProp) {
          setSelectedBank(bankFromProp);
        }
      } else {
        const sberbank = availableBanks.find((bank) => bank === 'Сбербанк');
        if (sberbank) {
          setSelectedBank(sberbank);
        } else if (availableBanks[0]) {
          setSelectedBank(availableBanks[0]);
        }
      }
    }
  }, [availableBanks, selectedBank, fromBank]);

  // Reset step index only when device, bank, or method actually changes
  // Use refs to track previous values to avoid resetting on re-renders
  const prevDeviceRef = useRef<DeviceType | null>(null);
  const prevBankRef = useRef<DepositBank | null>(null);
  const prevMethodRef = useRef<DepositMethod | null>(method);

  useEffect(() => {
    const deviceChanged =
      prevDeviceRef.current !== null &&
      prevDeviceRef.current !== selectedDevice;
    const bankChanged =
      prevBankRef.current !== null && prevBankRef.current !== selectedBank;
    const methodChanged = prevMethodRef.current !== method;

    if (deviceChanged || bankChanged || methodChanged) {
      handleStepIndexChange(0);
    }

    // Update refs
    prevDeviceRef.current = selectedDevice;
    prevBankRef.current = selectedBank;
    prevMethodRef.current = method;
  }, [selectedDevice, selectedBank, method, depositId, handleStepIndexChange]);

  useEffect(() => {
    if (isOpen) {
      trackEvent(AnalyticsEvents.INSTRUCTIONS_OPENED, {
        order_id: depositId,
        payment_method: method ?? '',
      });
    }
  }, [isOpen, depositId, method]);

  const handleDeviceChange = (newDevice: DeviceType) => {
    setSelectedDevice(newDevice);

    trackEvent(AnalyticsEvents.INSTRUCTIONS_DEVICE_SELECTED, {
      order_id: depositId,
      payment_method: method ?? '',
      device: newDevice,
    });
  };

  const imageSlides: InstructionSlide[] = useMemo(() => {
    if (!method) return [];

    // Handle VND_TG_QR (QR method, with bank)
    if (isQRMethod(method)) {
      const basePath = method === 'VND_TG_QR' ? 'vnd' : 'php';
      const bankForImages =
        selectedBank ?? availableBanks[0] ?? DepositBank.Values.Сбербанк;

      const bankMap: Record<DepositBank, string> = {
        Сбербанк: 'sber',
        ВТБ: 'vtb',
        'Т-Банк': 'tbank',
        'МТС Банк': 'mts',
        'Альфа банк': 'alfa',
      };
      const bankName = bankMap[bankForImages];

      // VND structure: vnd[bank][device][step]
      const baseCallouts = paymentInstructionCallouts as Record<
        string,
        unknown
      >;
      const bankCallouts = (
        baseCallouts[basePath] as Record<string, unknown>
      )?.[bankName] as Record<string, unknown> | undefined;

      const isVndVtb = bankForImages === DepositBank.Values.ВТБ;
      const isVndMts = bankForImages === DepositBank.Values['МТС Банк'];

      const allowedSteps: number[] | null = (() => {
        if (isVndVtb) return isMobile ? [6, 7, 8, 11] : [1, 2, 5];
        if (isVndMts) return isMobile ? [1, 2, 3, 5] : [1, 2, 4, 5];
        return null;
      })();

      const isStepAllowed = (step: number): boolean => {
        if (allowedSteps) return allowedSteps.includes(step);
        return isMobile ? step >= 4 : step <= 3;
      };

      const deviceCallouts = bankCallouts?.[selectedDevice];
      if (!deviceCallouts) {
        if (allowedSteps) {
          return allowedSteps.map((step) => ({
            kind: 'image' as const,
            src: getS3ImageUrl(method, selectedDevice, bankForImages, step),
            alt: t('payment_guide_step_image_alt', { step }),
            callouts: [],
          }));
        }
        return [
          {
            kind: 'image',
            src: getS3ImageUrl(method, selectedDevice, bankForImages, 1),
            alt: t('payment_guide_step_image_alt', { step: 1 }),
            callouts: [],
          },
        ];
      }

      const stepNumbers = Object.keys(deviceCallouts)
        .map(Number)
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b);

      const filteredStepNumbers = stepNumbers.filter(isStepAllowed);

      if (filteredStepNumbers.length === 0) {
        const fallbackStep = stepNumbers[0];

        if (!fallbackStep) {
          return [
            {
              kind: 'image',
              src: getS3ImageUrl(method, selectedDevice, bankForImages, 1),
              alt: t('payment_guide_step_image_alt', { step: 1 }),
              callouts: [],
            },
          ];
        }

        return [
          {
            kind: 'image',
            src: getS3ImageUrl(
              method,
              selectedDevice,
              bankForImages,
              fallbackStep,
            ),
            alt: t('payment_guide_step_image_alt', { step: fallbackStep }),
            callouts: [],
          },
        ];
      }

      return filteredStepNumbers.map((step) => {
        const callouts = getCalloutsFromJson(
          t,
          method,
          bankForImages,
          selectedDevice,
          step,
          credentials,
          amount,
          currency,
          isMobile,
        );

        return {
          kind: 'image' as const,
          src: getS3ImageUrl(method, selectedDevice, bankForImages, step),
          alt: t('payment_guide_step_image_alt', { step }),
          callouts,
        };
      });
    }

    if (method === 'TJS_TG') {
      const bankForImages =
        selectedBank ?? availableBanks[0] ?? DepositBank.Values.ВТБ;

      const bankMap: Record<DepositBank, string> = {
        Сбербанк: 'sber',
        ВТБ: 'vtb',
        'Т-Банк': 'tbank',
        'МТС Банк': 'mts',
        'Альфа банк': 'alfa',
      };

      const bankName = bankMap[bankForImages];
      const variantKey = tjsTransferType === 'CARD' ? 'card' : 'sbp';

      const baseCallouts = paymentInstructionCallouts as Record<
        string,
        unknown
      >;
      const variantCallouts = (baseCallouts.tjs as Record<string, unknown>)?.[
        variantKey
      ] as Record<string, unknown> | undefined;
      const bankCallouts = variantCallouts?.[bankName] as
        | Record<string, unknown>
        | undefined;
      const deviceCallouts = bankCallouts?.[selectedDevice] as
        | Record<string, unknown>
        | undefined;

      if (!deviceCallouts) {
        return [
          {
            kind: 'image',
            src: getS3ImageUrl(
              method,
              selectedDevice,
              bankForImages,
              1,
              tjsTransferType,
            ),
            alt: t('payment_guide_step_image_alt', { step: 1 }),
            callouts: [],
          },
        ];
      }

      const stepNumbers = Object.keys(deviceCallouts)
        .map(Number)
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b);

      if (stepNumbers.length === 0) {
        stepNumbers.push(1);
      }

      return stepNumbers.map((step) => {
        const callouts = getCalloutsFromJson(
          t,
          method,
          bankForImages,
          selectedDevice,
          step,
          credentials,
          amount,
          currency,
          isMobile,
        );

        return {
          kind: 'image' as const,
          src: getS3ImageUrl(
            method,
            selectedDevice,
            bankForImages,
            step,
            tjsTransferType,
          ),
          alt: t('payment_guide_step_image_alt', { step }),
          callouts,
        };
      });
    }

    if (method === 'MOBCOM') {
      const bankForImages =
        selectedBank ?? availableBanks[0] ?? DepositBank.Values.ВТБ;

      const bankMap: Record<DepositBank, string> = {
        Сбербанк: 'sber',
        ВТБ: 'vtb',
        'Т-Банк': 'tbank',
        'МТС Банк': 'mts',
        'Альфа банк': 'alfa',
      };

      const bankName = bankMap[bankForImages];

      const baseCallouts = paymentInstructionCallouts as Record<
        string,
        unknown
      >;
      const bankCallouts = baseCallouts.mobcom as
        | Record<string, unknown>
        | undefined;
      const deviceCallouts = bankCallouts?.[bankName] as
        | Record<string, unknown>
        | undefined;
      const finalCallouts = deviceCallouts?.[selectedDevice] as
        | Record<string, unknown>
        | undefined;

      if (!finalCallouts) {
        return [
          {
            kind: 'image',
            src: getS3ImageUrl(method, selectedDevice, bankForImages, 1),
            alt: t('payment_guide_step_image_alt', { step: 1 }),
            callouts: [],
          },
        ];
      }

      const stepNumbers = Object.keys(finalCallouts)
        .map(Number)
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b);

      return stepNumbers.map((step) => {
        const callouts = getCalloutsFromJson(
          t,
          method,
          bankForImages,
          selectedDevice,
          step,
          credentials,
          amount,
          currency,
          isMobile,
        );

        return {
          kind: 'image' as const,
          src: getS3ImageUrl(method, selectedDevice, bankForImages, step),
          alt: t('payment_guide_step_image_alt', { step }),
          callouts,
        };
      });
    }

    // Handle SBP methods (with banks)
    if (!isSbpMethod(method)) return [];

    const bankForImages =
      selectedBank ?? availableBanks[0] ?? DepositBank.Values.ВТБ;

    const basePath = method === 'AMD_TG_SBP' ? 'amd-tg' : 'sbp';

    const bankMap: Record<DepositBank, string> = {
      Сбербанк: 'sber',
      ВТБ: 'vtb',
      'Т-Банк': 'tbank',
      'МТС Банк': 'mts',
      'Альфа банк': 'alfa',
    };

    const bankName = bankMap[bankForImages];

    const baseCallouts = paymentInstructionCallouts as Record<string, unknown>;
    const bankCallouts = baseCallouts[basePath] as
      | Record<string, unknown>
      | undefined;
    const deviceCallouts = bankCallouts?.[bankName] as
      | Record<string, unknown>
      | undefined;
    const finalCallouts = deviceCallouts?.[selectedDevice] as
      | Record<string, unknown>
      | undefined;

    if (!finalCallouts) {
      return [
        {
          kind: 'image',
          src: getS3ImageUrl(method, selectedDevice, bankForImages, 1),
          alt: t('payment_guide_step_image_alt', { step: 1 }),
          callouts: [],
        },
      ];
    }

    const stepNumbers = Object.keys(finalCallouts)
      .map(Number)
      .filter((n) => !isNaN(n))
      .sort((a, b) => a - b);

    return stepNumbers.map((step) => {
      const callouts = getCalloutsFromJson(
        t,
        method,
        bankForImages,
        selectedDevice,
        step,
        credentials,
        amount,
        currency,
        isMobile,
      );

      return {
        kind: 'image' as const,
        src: getS3ImageUrl(method, selectedDevice, bankForImages, step),
        alt: t('payment_guide_step_image_alt', { step }),
        callouts,
      };
    });
  }, [
    method,
    selectedDevice,
    selectedBank,
    availableBanks,
    tjsTransferType,
    credentials,
    amount,
    currency,
    t,
    isMobile,
  ]);

  const canGoPrev = stepIndex > 0;
  const canGoNext = stepIndex < (imageSlides?.length ?? 0) - 1;

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Prevent scrolling if user is swiping horizontally
    if (touchStartX.current !== null && touchStartY.current !== null) {
      const touch = e.touches[0];
      if (!touch) return;
      const deltaX = Math.abs(touch.clientX - touchStartX.current);
      const deltaY = Math.abs(touch.clientY - touchStartY.current);

      // If horizontal swipe is more prominent than vertical, prevent default scroll
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;

    const touch = e.changedTouches[0];
    if (!touch) return;
    const touchEndX = touch.clientX;
    const touchEndY = touch.clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    if (absDeltaX > absDeltaY && absDeltaX > minSwipeDistance) {
      if (deltaX > 0 && canGoPrev) {
        // Swipe right - go to previous
        handleStepIndexChange((prev) => Math.max(0, prev - 1));
      } else if (deltaX < 0 && canGoNext) {
        // Swipe left - go to next
        handleStepIndexChange((prev) =>
          Math.min(imageSlides.length - 1, prev + 1),
        );
      }
    }

    // Reset touch start
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='full'>
      <ModalOverlay bg='rgba(0, 0, 0, 0.85)' backdropFilter='blur(4px)' />
      <ModalContent
        bg='grey.background'
        color='grey.primaryText'
        border='none'
        borderRadius={0}
        h='100vh'
        maxH='100vh'
        m={0}
      >
        <ModalHeader
          padding={['16px', '24px']}
          borderBottom='1px solid'
          borderColor='grey.border'
          position='sticky'
          alignItems='center'
          justifyContent='space-between'
          top={0}
          bg='grey.background'
          zIndex={1}
          display='flex'
        >
          <Box w='40px' />
          <Text
            fontSize={['lg', '3xl']}
            fontWeight='semibold'
            flex='1'
            textAlign='center'
          >
            {t('payment_instructions')}
          </Text>
          <IconButton
            aria-label={t('close')}
            icon={<CloseIcon boxSize='30px' />}
            onClick={handleClose}
            variant='ghost'
            color='grey.primaryText'
            borderRadius='full'
            border='none'
            _active={{ border: 'none', boxShadow: 'none' }}
            _focus={{ border: 'none', boxShadow: 'none' }}
            w='40px'
            minW='40px'
            size='sm'
            boxShadow='none'
          />
        </ModalHeader>
        <ModalBody
          display='flex'
          flexDirection='column'
          padding={0}
          overflow='hidden'
        >
          <Flex flex='1' direction='column' minH={0}>
            <Box
              px={['16px', '24px']}
              pt={['16px', '20px']}
              pb={['12px', '16px']}
            >
              <HStack
                spacing='24px'
                justify={isTgQr ? 'center' : 'space-between'}
                alignItems='center'
                w='100%'
                maxW='960px'
                mx='auto'
              >
                {/* Device Selection */}
                <VStack
                  spacing='12px'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Text fontSize={['sm', 'md']} fontWeight='semibold'>
                    {t('payment_guide_device_selection')}
                  </Text>
                  <ButtonGroup
                    isAttached
                    variant='paymentSelectorGray'
                    size={isMobile ? 'sm' : 'md'}
                    sx={{
                      '& > button:not(:first-of-type)': {
                        borderLeft: 'none',
                      },
                      '& > button:not(:last-of-type)': {
                        borderRight: 'none',
                      },
                      '& > button': {
                        px: isMobile ? ['12px', '16px'] : '24px',
                        fontSize: isMobile ? 'xs' : ['sm', 'md'],
                        h: isMobile ? '32px' : '40px',
                      },
                    }}
                  >
                    {(['android', 'ios', 'desktop'] as DeviceType[])
                      .filter((device) => {
                        if (isMobile) return device !== 'desktop';
                        if (isTgQr) return device !== 'desktop';
                        return true;
                      })
                      .map((device) => {
                        const isSelected = device === selectedDevice;
                        return (
                          <Button
                            key={device}
                            variant='paymentSelectorGray'
                            isActive={isSelected}
                            aria-pressed={isSelected}
                            onClick={() => handleDeviceChange(device)}
                          >
                            {t(`payment_guide_device_${device}`)}
                          </Button>
                        );
                      })}
                  </ButtonGroup>
                </VStack>

                {/* Bank Selection */}
                {availableBanks.length > 0 && !isTgQr && (
                  <VStack
                    alignItems='center'
                    justifyContent='center'
                    spacing='12px'
                  >
                    <Text fontSize={['sm', 'md']} fontWeight='semibold'>
                      {t('payment_guide_bank_selection')}
                    </Text>
                    <ButtonGroup
                      isAttached
                      variant='paymentSelectorGray'
                      size={isMobile ? 'sm' : 'md'}
                      sx={{
                        '& > button:not(:first-of-type)': {
                          borderLeft: 'none',
                        },
                        '& > button:not(:last-of-type)': {
                          borderRight: 'none',
                        },
                        '& > button': {
                          px: isMobile ? ['10px', '14px'] : '24px',
                          fontSize: isMobile ? 'xs' : ['sm', 'md'],
                          h: isMobile ? '32px' : '40px',
                        },
                      }}
                    >
                      {availableBanks.map((bank) => {
                        const isSelected = bank === selectedBank;
                        return (
                          <Button
                            key={bank}
                            variant='paymentSelectorGray'
                            isActive={isSelected}
                            aria-pressed={isSelected}
                            onClick={() => setSelectedBank(bank)}
                          >
                            {bank}
                          </Button>
                        );
                      })}
                    </ButtonGroup>
                  </VStack>
                )}
              </HStack>
            </Box>

            <Box
              flex='1'
              minH={0}
              maxH='70vh'
              w='100%'
              position='relative'
              overflow='hidden'
              borderTop='1px solid'
              borderTopColor='rgba(255, 255, 255, 0.10)'
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <Flex
                w={`${imageSlides.length * 100}%`}
                h='100%'
                transform={`translateX(-${
                  stepIndex * (100 / imageSlides.length)
                }%)`}
                transition='transform 400ms cubic-bezier(0.4, 0, 0.2, 1)'
                willChange='transform'
              >
                {imageSlides.map((slide, idx) => (
                  <Flex
                    key={idx}
                    w={`${100 / imageSlides.length}%`}
                    flex='0 0 auto'
                    h='100%'
                    align='center'
                    justify='center'
                    px={slide.kind === 'image' ? 0 : ['16px', '24px']}
                    pt={
                      slide.kind === 'image'
                        ? ['16px', '24px']
                        : ['20px', '32px']
                    }
                    pb={slide.kind === 'image' ? 0 : ['20px', '32px']}
                  >
                    {slide.kind === 'image' ? (
                      <ImageSlideWithCallouts
                        src={slide.src}
                        alt={slide.alt}
                        callouts={slide.callouts ?? []}
                      />
                    ) : (
                      <VStack
                        align='start'
                        spacing='12px'
                        w='100%'
                        maxW='760px'
                      >
                        <Text fontSize={['xl', '3xl']} fontWeight='semibold'>
                          {slide.title}
                        </Text>
                        <Text fontSize={['md', 'lg']} color='grey.tertiaryText'>
                          {slide.description}
                        </Text>
                      </VStack>
                    )}
                  </Flex>
                ))}
              </Flex>

              {/* Navigation arrows positioned on sides - hidden on mobile */}
              <IconButton
                aria-label={t('payment_guide_prev')}
                icon={<ArrowLeftIcon boxSize='28px' stroke='currentColor' />}
                isDisabled={!canGoPrev}
                onClick={() =>
                  handleStepIndexChange((prev) => Math.max(0, prev - 1))
                }
                variant='ghost'
                color='grey.primaryText'
                borderRadius='full'
                bg='rgba(0, 0, 0, 0.1)'
                border='none'
                _hover={{ bg: 'rgba(0, 0, 0, 0.15)', border: 'none' }}
                _active={{ border: 'none', boxShadow: 'none' }}
                _focus={{ border: 'none', boxShadow: 'none' }}
                position='absolute'
                left={['8px', '16px']}
                top='50%'
                transform='translateY(-50%)'
                zIndex={10}
                size='lg'
                display={['none', 'flex']}
                boxShadow='none'
              />
              <IconButton
                aria-label={t('payment_guide_next')}
                icon={<ArrowRightIcon boxSize='28px' stroke='currentColor' />}
                isDisabled={!canGoNext}
                onClick={() =>
                  handleStepIndexChange((prev) =>
                    Math.min(imageSlides.length - 1, prev + 1),
                  )
                }
                variant='ghost'
                color='grey.primaryText'
                borderRadius='full'
                bg='rgba(0, 0, 0, 0.1)'
                border='none'
                _hover={{ bg: 'rgba(0, 0, 0, 0.15)', border: 'none' }}
                _active={{ border: 'none', boxShadow: 'none' }}
                _focus={{ border: 'none', boxShadow: 'none' }}
                position='absolute'
                right={['8px', '16px']}
                top='50%'
                transform='translateY(-50%)'
                zIndex={10}
                size='lg'
                display={['none', 'flex']}
                boxShadow='none'
              />
            </Box>

            <Box px={['16px', '24px']} py={['24px', '30px']}>
              <VStack w='100%' maxW='960px' mx='auto' spacing='12px'>
                <Flex w='100%' justify='center' align='center' gap='14px'>
                  <Text fontSize='sm' color='grey.secondaryText'>
                    {t('payment_guide_step_counter', {
                      current: stepIndex + 1,
                      total: imageSlides.length,
                    })}
                  </Text>
                  <HStack
                    spacing='10px'
                    justify='center'
                    px='12px'
                    py='10px'
                    borderRadius='full'
                    bg='rgba(0, 0, 0, 0.2)'
                    border='1px solid'
                    borderColor='rgba(255, 255, 255, 0.14)'
                  >
                    {imageSlides.map((_, idx) => {
                      const isActive = idx === stepIndex;
                      return (
                        <Box
                          key={idx}
                          as='button'
                          type='button'
                          aria-current={isActive ? 'step' : undefined}
                          aria-label={t('payment_guide_go_to_step', {
                            step: idx + 1,
                          })}
                          onClick={() => handleStepIndexChange(idx)}
                          w={isActive ? '28px' : '14px'}
                          h='14px'
                          borderRadius='full'
                          transition='all 200ms ease'
                          bg={
                            isActive
                              ? 'rgba(255, 255, 255, 0.95)'
                              : 'rgba(255, 255, 255, 0.35)'
                          }
                          border='1px solid'
                          borderColor={
                            isActive
                              ? 'rgba(255, 255, 255, 0.75)'
                              : 'rgba(255, 255, 255, 0.25)'
                          }
                          boxShadow={
                            isActive
                              ? '0 0 0 4px rgba(255, 255, 255, 0.10)'
                              : 'none'
                          }
                          _focusVisible={{
                            outline: 'none',
                            boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.25)',
                          }}
                        />
                      );
                    })}
                  </HStack>
                </Flex>
              </VStack>
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
